// src/pages/FacultyLogin.jsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";

export default function FacultyLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [focused, setFocused] = useState("");
  const { facultyLogin } = useAuth();
  const navigate = useNavigate();

  async function handleLogin() {
    if (!email || !password) return setError("Please fill all fields");
    try {
      setLoading(true);
      setError("");
      const result = await facultyLogin(email, password);

      // Check if this is a first-time login requiring password reset
      const res = await api.get(`/faculty/${result.user.uid}/profile`);
      const userDoc = res ? { id: res.id, ...(res.data || res) } : null;
      if (userDoc?.mustResetPassword === true) {
        navigate("/reset-password");
        return;
      }

      navigate("/dashboard");
    } catch {
      setError("Invalid user name or password");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #f0f7ff 0%, #e8f4fd 50%, #f5f0ff 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Nunito', sans-serif" }}>

      {/* Decorative bg shapes */}
      <div style={{ position: "absolute", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)", top: -200, right: -100, pointerEvents: "none" }} />
      <div style={{ position: "absolute", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(124,58,237,0.05) 0%, transparent 70%)", bottom: -100, left: -100, pointerEvents: "none" }} />

      <div style={{ width: "100%", maxWidth: 460, padding: "0 20px" }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ display: "inline-flex", alignItems: "center", background: "white", borderRadius: 12, padding: "10px 20px 10px 10px", boxShadow: "0 4px 20px rgba(37,99,235,0.12)", gap: 12 }}>
            <img src="/apdch_logo.png" alt="APDCH Logo" style={{ width: 72, height: 72, objectFit: "contain" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "left" }}>
              <span style={{ fontWeight: 900, fontSize: 17, color: "#7b1a1a", letterSpacing: 0.3, lineHeight: 1.3 }}>
                Adhiparasakthi Dental College &amp; Hospital
              </span>
              <span style={{ fontWeight: 700, fontSize: 13, color: "#7b1a1a", letterSpacing: 2, opacity: 0.75 }}>
                MELMARUVATHUR
              </span>
            </div>
          </div>
        </div>

        <div style={{ background: "#fff", borderRadius: 24, padding: "40px 36px", boxShadow: "0 8px 40px rgba(15,37,87,0.1)", border: "1px solid rgba(37,99,235,0.08)" }}>

          {/* Faculty badge */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#eaf1ff", border: "1px solid #bfdbfe", borderRadius: 20, padding: "4px 14px", marginBottom: 24 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#2563eb" }} />
            <span style={{ color: "#1d4ed8", fontSize: 14, fontWeight: 700, letterSpacing: 1 }}>FACULTY PORTAL</span>
          </div>

          <h2 style={{ color: "#0f2557", fontSize: 28, fontWeight: 800, marginBottom: 6 }}>Faculty Login</h2>
          <p style={{ color: "#64748b", fontSize: 16, marginBottom: 28 }}>Enter your credentials to access your profile</p>

          {error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 14px", color: "#dc2626", fontSize: 15, marginBottom: 18 }}>
              {error}
            </div>
          )}

          {localStorage.getItem("session_terminated_debug") && (
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "10px 14px", color: "#b45309", fontSize: 15, marginBottom: 18 }}>
              ⚠️ Debug Info: {localStorage.getItem("session_terminated_debug")}
              <button 
                onClick={() => { localStorage.removeItem("session_terminated_debug"); window.location.reload(); }}
                style={{ background: "transparent", border: "none", color: "#2563eb", cursor: "pointer", marginLeft: 10, textDecoration: "underline", fontWeight: 700 }}
              >
                Clear
              </button>
            </div>
          )}

          {/* Email */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: "#374151", fontSize: 15, fontWeight: 700, display: "block", marginBottom: 8 }}>User name</label>
            <div style={{ display: "flex", alignItems: "center", background: focused === "email" ? "#eaf1ff" : "#f8fafc", border: `1.5px solid ${focused === "email" ? "#2563eb" : "#e2e8f0"}`, borderRadius: 12, padding: "0 16px", transition: "all 0.2s" }}>
              <input
                type="text" placeholder="Enter your user name" value={email}
                onChange={e => setEmail(e.target.value)}
                onFocus={() => setFocused("email")} onBlur={() => setFocused("")}
                style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "#1e293b", fontSize: 17, padding: "13px 0", fontFamily: "'Nunito', sans-serif" }}
              />
            </div>
          </div>

          {/* Password */}
          <div style={{ marginBottom: 10 }}>
            <label style={{ color: "#374151", fontSize: 15, fontWeight: 700, display: "block", marginBottom: 8 }}>Password</label>
            <div style={{ display: "flex", alignItems: "center", background: focused === "pass" ? "#eaf1ff" : "#f8fafc", border: `1.5px solid ${focused === "pass" ? "#2563eb" : "#e2e8f0"}`, borderRadius: 12, padding: "0 16px", transition: "all 0.2s" }}>
              <input
                type={showPass ? "text" : "password"} placeholder="Enter your password" value={password}
                onChange={e => setPassword(e.target.value)}
                onFocus={() => setFocused("pass")} onBlur={() => setFocused("")}
                onKeyDown={e => e.key === "Enter" && handleLogin()}
                style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "#1e293b", fontSize: 17, padding: "13px 0", fontFamily: "'Nunito', sans-serif" }}
              />
              <span onClick={() => setShowPass(v => !v)} style={{ cursor: "pointer", fontSize: 18 }}>{showPass ? "🙈" : "👁️"}</span>
            </div>
          </div>

          <div style={{ textAlign: "right", marginBottom: 28 }}>
            <span style={{ color: "#2563eb", fontSize: 15, cursor: "pointer", fontWeight: 600 }}>Forgot password?</span>
          </div>

          <button
            onClick={handleLogin} disabled={loading}
            style={{ width: "100%", background: loading ? "#93c5fd" : "linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)", border: "none", borderRadius: 12, padding: "14px", color: "#fff", fontSize: 18, fontWeight: 800, cursor: loading ? "not-allowed" : "pointer", boxShadow: "0 4px 16px rgba(37,99,235,0.3)", fontFamily: "'Nunito', sans-serif" }}
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "22px 0" }}>
            <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
            <span style={{ color: "#94a3b8", fontSize: 14 }}>OR</span>
            <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
          </div>

          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "14px", textAlign: "center" }}>
            <span style={{ color: "#64748b", fontSize: 15 }}>Admin? </span>
            <span onClick={() => navigate("/admin")} style={{ color: "#2563eb", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>Admin Login →</span>
          </div>
        </div>

        <p style={{ color: "#94a3b8", fontSize: 14, textAlign: "center", marginTop: 20 }}>
          Contact admin if you don't have an account
        </p>
      </div>
    </div>
  );
}
